import { Pagination, PaginationProps, Table, TableProps } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import styled from 'styled-components'
import { themes } from '@theme'
import { useMemo, useEffect } from 'react'
import { RowClassName } from 'rc-table/lib/interface'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

interface ITableProps {
  columns: ColumnsType<any>
  dataSource: any
  key?: number | string
  rowKey?: any
  paginationProps?: PaginationProps
  onRowClick?: (record: any) => void
  sticky?: boolean
  totalResults?: number
  className?: string
  tableProps?: TableProps<any>
  hasIndexColumn?: boolean
  rowClassName?: string | RowClassName<any>
}

export const SharedTable = ({
  columns,
  dataSource,
  key,
  rowKey,
  paginationProps,
  onRowClick,
  sticky,
  totalResults,
  className,
  hasIndexColumn = true,
  tableProps,
  rowClassName,
}: ITableProps) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  
  const pageFromUrl = Number(searchParams.get('page')) || 1
  
  const currentPage = pageFromUrl
  const { pageSize } = paginationProps || {}

  const handleRowClick = (record: any) => {
    if (onRowClick) {
      onRowClick(record)
    }
  }

  const handlePageChange = (page: number, pageSize?: number) => {
    // Gọi onChange trước - component cha sẽ xử lý async logic và cập nhật currentPage
    // URL sẽ được cập nhật trong useEffect khi currentPage từ props thay đổi
    if (paginationProps?.onChange) {
      paginationProps.onChange(page, pageSize || paginationProps.pageSize || 10)
    }
  }
  
  // Cập nhật URL khi currentPage từ props thay đổi (sau khi API thành công)
  useEffect(() => {
    const currentFromProps = paginationProps?.current
    if (currentFromProps && currentFromProps !== pageFromUrl) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev)
        newParams.set('page', currentFromProps.toString())
        return newParams
      })
    }
  }, [paginationProps?.current])

  useEffect(() => {
    if (pageFromUrl && paginationProps?.onChange && pageFromUrl !== paginationProps.current) {
      paginationProps.onChange(pageFromUrl, paginationProps.pageSize || 10)
    }
  }, []) 

  useEffect(() => {
    const totalPages = paginationProps?.total && paginationProps?.pageSize 
      ? Math.ceil(paginationProps.total / paginationProps.pageSize) 
      : 0
    
    if (totalPages > 0 && currentPage > totalPages) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev)
        newParams.set('page', '1')
        return newParams
      })
      
      if (paginationProps?.onChange) {
        paginationProps.onChange(1, paginationProps.pageSize || 10)
      }
    }
  }, [paginationProps?.total, paginationProps?.pageSize, currentPage])


  const dataTable = useMemo(() => {
    if (!Array.isArray(dataSource) || !dataSource?.length) return []

    return dataSource.map((it, idx) => ({
      ...it,
      // If the list showing the NO (index) column --> using columns[0] because first column in system always display NO value
      ...(hasIndexColumn && {
        [(columns as any)?.[0]?.dataIndex]:
          currentPage && pageSize
            ? idx + 1 + (Number(currentPage) - 1) * Number(pageSize)
            : idx + 1,
      }),
    }))
  }, [columns, dataSource, hasIndexColumn, currentPage, pageSize])

  // Merge pagination props với current page từ URL hoặc từ props
  const mergedPaginationProps = useMemo(() => {
    if (!paginationProps) return undefined
    
    // Ưu tiên sử dụng current từ props nếu có, nếu không thì dùng từ URL
    const current = paginationProps.current !== undefined ? paginationProps.current : currentPage
    
    return {
      ...paginationProps,
      current: current,
      onChange: handlePageChange,
    }
  }, [paginationProps, currentPage])

  useEffect(() => {
    return () => {
      const params = new URLSearchParams(location.search)

      if (params.has('page')) {
        params.delete('page')

        navigate(
          {
            pathname: location.pathname,
            search: params.toString()
          },
          { replace: true }
        )
      }
    }
  }, [])

  return (
    <StyledTableWrapper $appTheme={themes.theme.light.colors.primary}>
      <Table
        className={`bg-gray-100 md:ml-3 mt-3 ${className}`}
        key={key}
        rowKey={rowKey}
        columns={columns}
        pagination={false}
        dataSource={dataTable}
        rowClassName={
          rowClassName
            ? rowClassName
            : (record, index) =>
                index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
        }
        onRow={(record, rowIndex) => {
          return {
            onClick: () => handleRowClick(record),
          }
        }}
        sticky={sticky}
        {...tableProps}
      />
      <div
        className={`flex ${
          !!totalResults ? 'justify-between' : 'justify-end'
        } mt-3 px-[0.5rem]`}
      >
        {!!totalResults ? (
          <span className="font-semibold p-2 border border-neutral-300">
            {totalResults} {totalResults > 1 ? 'results' : 'result'}
          </span>
        ) : null}
        {mergedPaginationProps?.total && <Pagination {...mergedPaginationProps} />}
      </div>
    </StyledTableWrapper>
  )
}

const StyledTableWrapper = styled.div<{
  $appTheme: string
}>`
  .ant-table-tbody > tr:hover {
    cursor: pointer;
  }

  .ant-pagination-item-active a {
    border-color: ${(p) => p.$appTheme};
  }
  .ant-pagination-item-active {
    font-weight: 500;
    background: #fff;
    border-color: ${(p) => p.$appTheme};
    a {
      color: ${(p) => p.$appTheme};
    }
  }

  .ant-table-thead > tr > th {
    position: relative;
    color: white;
    font-weight: 500;
    text-align: left;
    background: ${(p) => p.$appTheme};
    border-bottom: 2px solid #f0f0f0;
    transition: background 0.3s ease;
    padding: 0.75rem 1.25rem;
    font-size: 0.875rem;
  }
  .ant-table-tbody > tr > td {
    transition: background 0.3s;
    text-align: left;
    padding: 0.75rem 1.25rem;
    font-size: 0.875rem;
  }
  .ant-table-wrapper {
    overflow: auto;
    width: 100%;
    max-height: 63vh;
  }
  .ant-table-wrapper .ant-table {
    width: 100%;
  }

  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    width: auto;
    white-space: nowrap;
  }
  .ant-table-content > table {
    width: 100%;
    border-left: 2px solid #f0f0f0;
  }
  .ant-table-content {
    margin-right: 1.25rem;
  }

  .ant-table-thead {
    .ant-table-cell {
      word-break: auto-phrase;
      text-wrap: wrap;
      overflow-wrap: normal;
    }
  }

  .ant-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    .ant-pagination-prev {
      button {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &:hover,
      &:focus-visible {
        .ant-pagination-item-link {
          border-color: ${(p) => p.$appTheme};
          color: ${(p) => p.$appTheme};
        }
      }
    }
    .ant-pagination-next {
      button {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &:hover,
      &:focus-visible {
        .ant-pagination-item-link {
          border-color: ${(p) => p.$appTheme};
          color: ${(p) => p.$appTheme};
        }
      }
    }

    .ant-pagination-disabled {
      .ant-pagination-item-link {
        color: rgba(0, 0, 0, 0.25);
        border-color: #d9d9d9;
        cursor: not-allowed;
      }

      &:hover {
        .ant-pagination-item-link {
          color: rgba(0, 0, 0, 0.25);
          border-color: #d9d9d9;
          cursor: not-allowed;
        }
      }
    }

    .ant-pagination-item {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .ant-pagination-item:hover {
      border-color: ${(p) => p.$appTheme};
      a {
        color: ${(p) => p.$appTheme};
      }
    }

    .ant-table-thead
      > tr
      > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan]):before {
      position: absolute;
      top: 50%;
      right: 0;
      width: 1px;
      height: 1.6em;
      background-color: white !important;
      transform: translateY(-50%);
      transition: background-color 0.3s;
      content: '';
    }
  }
`